import React, { SVGProps, useMemo, useEffect } from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  Legend,
  ReferenceLine,
} from 'recharts';
import clsx from 'clsx';
import useMediaQuery from '../../hooks/useMediaQuery';
import { ceilNumberToTwoDigit, getMaxValue } from '../../helpers';
import {
  CheckboxChecked24Filled,
  CheckboxUnchecked24Regular,
} from '@fluentui/react-icons';
import { DataKey } from 'recharts/types/util/types';

interface BarProps {
  dataKey: DataKey<any>;
  fill?: string;
  label?: string;
  stackId?: string;
}

interface BarChartProps {
  valueFormatter?: (value: number) => string;
  data: any[];
  children: React.ReactElement<BarProps>[];
  tickFormatter?: (value: number) => string;
  classNameHeader?: string;
  classNameChart?: string;
  stacked?: boolean;
  barSize?: number;
  onDataKeysToggle?: (values: DataKey<any>[]) => void;
}

interface CustomTooltipProps extends TooltipProps<any, any> {
  valueFormatter?: (value: number) => string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = React.memo(
  ({ active, payload, valueFormatter }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="p-4 !pt-3 bg-white border shadow-md rounded-3xl">
        <p className="text-sm font-semibold text-black">
          {payload[0]?.payload?.largeName || payload[0]?.payload?.name}
        </p>
        {payload.map((p, k) => (
          <p
            key={k}
            style={{ color: p.color }}
            className="py-1 text-sm font-semibold"
          >
            {`${p.name}: ${valueFormatter ? valueFormatter(p.value) : p.value}`}
          </p>
        ))}
      </div>
    );
  },
);

CustomTooltip.displayName = 'CustomTooltip';

const getMinValue = (data: any[]): number => {
  let min = Infinity;
  data.forEach((item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (typeof value === 'number' && !isNaN(value)) {
        min = Math.min(min, value);
      }
    });
  });
  return min === Infinity ? 0 : min;
};

export const BarChart: React.FC<BarChartProps> = ({
  valueFormatter,
  data,
  children,
  tickFormatter,
  classNameHeader,
  classNameChart,
  stacked = false,
  barSize = 5,
  onDataKeysToggle,
}) => {
  const [disableBarIds, setDisableBarIds] = React.useState<DataKey<any>[]>([]);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const filteredData = useMemo(() => {
    return data.map((element) => {
      const clone = { ...element };
      Object.keys(clone).forEach((key) => {
        if (disableBarIds.includes(key)) {
          delete clone[key];
        }
      });
      return clone;
    });
  }, [data, disableBarIds]);

  const barProps = useMemo(
    () => React.Children.map(children, (child) => child.props) || [],
    [children],
  );

  const maxValue = useMemo(
    () => ceilNumberToTwoDigit(getMaxValue(filteredData)),
    [filteredData],
  );

  const minValue = useMemo(
    () => Math.floor(getMinValue(filteredData)),
    [filteredData],
  );

  const ticks = useMemo(() => {
    if (isNaN(maxValue) || isNaN(minValue)) return [];

    if (minValue < 0 && maxValue > 0) {
      const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
      const step = absMax / 2;
      return [-absMax, -step, 0, step, absMax];
    }

    if (maxValue <= 0) {
      const step = Math.abs(minValue) / 4;
      return Array.from({ length: 5 }, (_, i) => minValue + i * step);
    }

    const step = maxValue / 4;
    return Array.from({ length: 5 }, (_, i) => (i + 1) * step);
  }, [maxValue, minValue]);

  const toggleBar = (id: DataKey<any>) => {
    setDisableBarIds((prev) => {
      const newBarIds = prev.includes(id)
        ? prev.filter((barId) => barId !== id)
        : [...prev, id];
      return newBarIds;
    });
  };

  useEffect(() => {
    if (onDataKeysToggle) {
      const enabledBarIds = barProps
        .map((bar) => bar.dataKey.toString())
        .filter((id) => !disableBarIds.includes(id));
      onDataKeysToggle(enabledBarIds);
    }
  }, [disableBarIds, barProps, onDataKeysToggle]);

  return (
    <div>
      <div
        className={clsx('flex items-center justify-end gap-8', classNameHeader)}
      >
        {barProps.map((bar, index) => {
          const isChecked = !disableBarIds.includes(bar.dataKey);
          return (
            <label
              key={`${bar.dataKey.toString()}-${index}`}
              className="flex items-center gap-2 cursor-pointer"
              style={{ color: bar.fill }}
            >
              {isChecked ? (
                <CheckboxChecked24Filled className="w-6 h-6" />
              ) : (
                <CheckboxUnchecked24Regular className="w-6 h-6" />
              )}
              <span className="hidden text-sm font-medium text-gray-700 md:block">
                {bar.label}
              </span>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleBar(bar.dataKey)}
                className="hidden"
              />
            </label>
          );
        })}
      </div>
      <div className={clsx('w-full h-[400px]', classNameChart)}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={filteredData}
            margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
            barSize={barSize}
          >
            <CartesianGrid
              horizontal={true}
              vertical={false}
              stroke="#F1F0F4"
            />
            <XAxis
              interval="preserveStartEnd"
              dataKey={isMobile && data.length > 6 ? 'smName' : 'name'}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={tickFormatter}
              ticks={ticks}
              type="number"
              domain={[minValue, maxValue]}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip {...props} valueFormatter={valueFormatter} />
              )}
            />
            {React.Children.map(children, (child) =>
              !disableBarIds.includes(child.props.dataKey)
                ? React.cloneElement(child, {
                    key: child.props.dataKey.toString(),
                    stackId: stacked ? 'stack' : undefined,
                  })
                : null,
            )}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
